import React, {memo} from 'react';
import {Bar} from "react-chartjs-2";
import {ChartData, ChartOptions} from "chart.js";
import {formatNumber} from "../../../../components/helpers";

const BarChart = memo((
  {
    month,
    total,
    year,
    displayLegend = true,
    thickness = 25,
    height,
    fontSize = 11,
  }: {
    month: string[],
    total: any,
    year?: number,
    displayLegend?: boolean,
    thickness?: number,
    height?: any,
    fontSize?: number,
  }) => {
  const labels = [`Nombre VH année ${year - 1}`, `Nombre VH année ${year}`];
  const colors = ["#6e6e6e", "#FF3342FF"];
  const sumColors = ["#505050", "#b91826"];

  const totalData = total.map((data, key: number) => ({
    label: labels?.[key] || '',
    type: key === 0 ? "line" : "bar",
    data: data,
    ...(key === 0 ? {borderColor: colors?.[key] || "#939393"} : {}),
    backgroundColor: colors?.[key] || "#939393",
    hoverBackgroundColor: colors?.[key] || "#939393",
    barThickness: thickness,
  }));

  const barData: ChartData<any> = {
    labels: month,
    datasets: totalData,
  };

  const barOptions: ChartOptions<any> = {
    maintainAspectRatio: false,
    responsive: true,
    layout: {
      padding: {
        left: 20,
        right: 20,
        top: 15,
      },
    },
    interaction: {mode: "index", intersect: false},
    scales: {
      x: {
        ticks: {
          font: {
            size: fontSize
          }
        },
        stacked: true,
        grid: {
          display: false
        }
      },
      y: {
        ticks: {
          font: {
            size: fontSize
          },
          stepSize: 500,
          callback: function (value: number) {
            return formatNumber(this?.getLabelForValue(value));
          },
        }
      }
    },
    plugins: {
      ...(displayLegend ? {
        legend: {
          display: true,
          position: 'bottom',
          align: 'center',
          labels: {boxWidth: 12}
        },
        hover: {mode: false}
      } : {
        legend: {display: false}
      }),
      tooltip: {
        position: 'nearest',
        callbacks: {
          label: function (items) {
            const label = items?.dataset?.label;
            const value = formatNumber(items?.raw);
            return `${label}: ${value}`;
          }
        }
      }
    },
  };

  const barValuePlugin = {
    id: 'barValuePlugin',
    afterDatasetsDraw: (chart) => {
      const {ctx, data} = chart;
      data.datasets.forEach((dataset, datasetIndex) => {
        const meta = chart.getDatasetMeta(datasetIndex);
        if (!meta.hidden) {
          meta.data.forEach((bar, index) => {
            const value = dataset.data[index] || 0;
            ctx.save();
            ctx.textAlign = 'center';
            ctx.textBaseline = 'bottom';
            ctx.fillStyle = sumColors?.[datasetIndex] || '#444';
            ctx.font = `small ${fontSize}px Arial`;
            ctx.fillText(`${value !== 0 ? formatNumber(value) : ''}`, bar.x, bar.y - 1);
            ctx.restore();
          });
        }
      });
    }
  };

  return <Bar data={barData} options={barOptions} height={height} plugins={[barValuePlugin]}/>;
});

export default BarChart;